
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Search, 
  Filter, 
  Eye, 
  User, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Video, 
  Phone, 
  Stethoscope,
  CalendarRange,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { useTreatmentSessions } from '@/hooks/useTreatmentSessions';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { TreatmentSession, SessionStatus } from '@/types/treatmentSession';
import SessionDetailsModal from './SessionDetailsModal';

// Mock patient data for filtering
const mockPatients = [
  { id: 101, name: 'John Doe' },
  { id: 102, name: 'Jane Smith' },
  { id: 103, name: 'Robert Johnson' },
  { id: 104, name: 'Alice Williams' },
];

// Session type mapping
const sessionTypeMap = {
  'in_clinic': { label: 'In-Clinic', icon: Stethoscope },
  'video': { label: 'Video Call', icon: Video },
  'audio': { label: 'Audio Call', icon: Phone },
};

const TreatmentSessionsPage: React.FC = () => {
  // Using a default plan ID for demonstration, in a real app this would be fetched or not needed
  const { sessions: allSessions, isLoading, error } = useTreatmentSessions('all');
  
  const [filteredSessions, setFilteredSessions] = useState<TreatmentSession[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [selectedSession, setSelectedSession] = useState<TreatmentSession | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  
  const navigate = useNavigate();

  // Filter sessions based on search and filter criteria
  useEffect(() => {
    if (!allSessions) return;
    
    let result = [...allSessions];
    
    // Search by patient name or treatment title
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(session => 
        session.doctorName.toLowerCase().includes(searchLower) || 
        session.title.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by patient
    if (selectedPatient) {
      result = result.filter(session => String(session.doctorId) === selectedPatient);
    }
    
    // Filter by status
    if (selectedStatus) {
      result = result.filter(session => session.status === selectedStatus);
    }
    
    // Filter by session type (would need to be added to the TreatmentSession type)
    if (selectedType) {
      result = result.filter(session => 
        (session as any).sessionType === selectedType
      );
    }
    
    // Filter by date range
    if (dateFrom) {
      result = result.filter(session => 
        isAfter(parseISO(session.date), new Date(dateFrom.setHours(0, 0, 0, 0)))
      );
    }
    
    if (dateTo) {
      result = result.filter(session => 
        isBefore(parseISO(session.date), new Date(dateTo.setHours(23, 59, 59, 999)))
      );
    }
    
    setFilteredSessions(result);
  }, [allSessions, searchTerm, selectedPatient, selectedStatus, selectedType, dateFrom, dateTo]);

  const handleViewDetails = (session: TreatmentSession) => {
    setSelectedSession(session);
    setIsDetailsModalOpen(true);
  };

  const handleDateFromSelect = (date: Date | undefined) => {
    setDateFrom(date);
  };

  const handleDateToSelect = (date: Date | undefined) => {
    setDateTo(date);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedPatient(null);
    setSelectedStatus(null);
    setSelectedType(null);
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const getStatusBadge = (status: SessionStatus) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      case 'upcoming':
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Upcoming
          </Badge>
        );
      case 'rescheduled':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
            <CalendarRange className="h-3 w-3 mr-1" />
            Rescheduled
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get session type badge and icon (mock implementation)
  const getSessionTypeBadge = (session: TreatmentSession) => {
    // Mock the session type for demo purposes
    const sessionType = (session.id % 3 === 0) ? 'video' : 
                       (session.id % 3 === 1) ? 'audio' : 'in_clinic';
    
    const { label, icon: Icon } = sessionTypeMap[sessionType as keyof typeof sessionTypeMap];
    
    return (
      <Badge variant="outline" className="flex items-center space-x-1">
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Treatment Sessions</h1>
        <Card>
          <CardContent className="p-8 flex justify-center items-center">
            <p>Loading treatment sessions...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Treatment Sessions</h1>
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <p className="text-red-500 mb-4">Error loading treatment sessions</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">Treatment Sessions</h1>
        <Button onClick={() => navigate('/doctor-area/create-treatment-plan/new')}>
          <FileText className="mr-2 h-4 w-4" />
          Create New Treatment Plan
        </Button>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle>Sessions Management</CardTitle>
          <CardDescription>
            View and manage all your patient treatment sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search" 
                placeholder="Search by patient or treatment..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Select value={selectedPatient || undefined} onValueChange={setSelectedPatient}>
                <SelectTrigger className="w-[180px]">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select Patient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Patients</SelectItem>
                  {mockPatients.map(patient => (
                    <SelectItem key={patient.id} value={String(patient.id)}>
                      {patient.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedStatus || undefined} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedType || undefined} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="in_clinic">In-Clinic</SelectItem>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="audio">Audio Call</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px]">
                    <Calendar className="h-4 w-4 mr-2" />
                    {dateFrom ? format(dateFrom, 'PP') : 'Date From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={dateFrom}
                    onSelect={handleDateFromSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px]">
                    <Calendar className="h-4 w-4 mr-2" />
                    {dateTo ? format(dateTo, 'PP') : 'Date To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={dateTo}
                    onSelect={handleDateToSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Button variant="ghost" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>

          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Sessions</TabsTrigger>
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="other">Cancelled/Rescheduled</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Treatment</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.length > 0 ? (
                      filteredSessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span>{session.doctorName}</span>
                            </div>
                          </TableCell>
                          <TableCell>{session.title}</TableCell>
                          <TableCell>
                            {format(parseISO(session.date), 'MMM dd, yyyy - h:mm a')}
                          </TableCell>
                          <TableCell>
                            {getSessionTypeBadge(session)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(session.status)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewDetails(session)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          No sessions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="upcoming">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Treatment</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.filter(s => s.status === 'upcoming').length > 0 ? (
                      filteredSessions
                        .filter(s => s.status === 'upcoming')
                        .map((session) => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>{session.doctorName}</span>
                              </div>
                            </TableCell>
                            <TableCell>{session.title}</TableCell>
                            <TableCell>
                              {format(parseISO(session.date), 'MMM dd, yyyy - h:mm a')}
                            </TableCell>
                            <TableCell>
                              {getSessionTypeBadge(session)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(session.status)}
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewDetails(session)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          No upcoming sessions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="completed">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Treatment</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.filter(s => s.status === 'completed').length > 0 ? (
                      filteredSessions
                        .filter(s => s.status === 'completed')
                        .map((session) => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>{session.doctorName}</span>
                              </div>
                            </TableCell>
                            <TableCell>{session.title}</TableCell>
                            <TableCell>
                              {format(parseISO(session.date), 'MMM dd, yyyy - h:mm a')}
                            </TableCell>
                            <TableCell>
                              {getSessionTypeBadge(session)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(session.status)}
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewDetails(session)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          No completed sessions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="other">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Treatment</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.filter(s => ['cancelled', 'rescheduled'].includes(s.status)).length > 0 ? (
                      filteredSessions
                        .filter(s => ['cancelled', 'rescheduled'].includes(s.status))
                        .map((session) => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>{session.doctorName}</span>
                              </div>
                            </TableCell>
                            <TableCell>{session.title}</TableCell>
                            <TableCell>
                              {format(parseISO(session.date), 'MMM dd, yyyy - h:mm a')}
                            </TableCell>
                            <TableCell>
                              {getSessionTypeBadge(session)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(session.status)}
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewDetails(session)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          No cancelled or rescheduled sessions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {selectedSession && (
        <SessionDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          session={selectedSession}
        />
      )}
    </div>
  );
};

export default TreatmentSessionsPage;
